/*
Purpose: Day Timeline layout — week-like time axis for a single day (no tutor column).
Sessions stack side-by-side when they overlap (e.g. multiple tutors at the same time).
Hours with no session stay short, the same as the week view.
*/

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment-timezone';

import { DENSITY } from '../constants';
import { initialsOf } from '../session-model';
import SessionCard from './SessionCard';

/** Same short row as the week view. Hours with no session stay at this height. */
const EMPTY_HOUR_HEIGHT_PX = 40;

/** Flat minimum for a compact card when sizing the hours it sits in. */
const COMPACT_MIN_CARD_HEIGHT_PX = 64;

/** Fallback card width until the node is measured. */
const FALLBACK_CARD_WIDTH_PX = 180;

/** Pad from the day column’s left edge. */
const EDGE_PAD_PX = 2;

/**
 * Estimate detailed day-card height (tutor, time, subjects, markers, every student).
 */
function estimateDetailedCardHeightPx(session) {
  const padding = 18;
  const tutor = session.tutorName ? 20 : 0;
  const head = 20;
  const subjects = Array.from(
    new Set((session.students || []).map((student) => student.subjectName).filter(Boolean))
  );
  const subjectLine = subjects.length > 0 ? 16 : 0;
  const hasMarkers =
    session.deferredCount > 0 ||
    session.oneOffCount > 0 ||
    session.makeUpCount > 0 ||
    session.isOneOffClass ||
    session.isMakeUpClass;
  const markers = hasMarkers ? 22 : 0;
  const students = session.students || [];
  const studentBlockGap = 6;
  let studentsHeight = 24;
  if (students.length > 0) {
    studentsHeight = students.reduce((total, student, index) => {
      const hasExtraTag = Boolean(student.isReassigned || student.isPaused);
      const row =
        18 +
        (student.subjectName ? 14 : 0) +
        (hasExtraTag ? 40 : 24) +
        (index > 0 ? studentBlockGap : 0);
      return total + row;
    }, 0);
  }
  return padding + tutor + head + subjectLine + markers + 6 + studentsHeight;
}

function hourLabel(hour) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function sessionEndHour(session) {
  if (session.endHour != null && session.endHour > session.startHour) {
    return session.endHour;
  }
  return session.startHour + 1;
}

/** Pack overlapping sessions into side-by-side columns (multi-tutor same slot). */
function layoutDaySessions(sessions, windowStart, windowEnd, density, measuredHeights) {
  const sorted = [...sessions].sort((a, b) => {
    const startDiff = a.startHour - b.startHour;
    if (startDiff !== 0) {
      return startDiff;
    }
    return sessionEndHour(a) - sessionEndHour(b);
  });

  const columnEnds = [];
  return sorted
    .map((session) => {
      const start = Math.max(session.startHour, windowStart);
      const end = Math.min(sessionEndHour(session), windowEnd);
      let column = columnEnds.findIndex((endsAt) => endsAt <= start + 0.001);
      if (column === -1) {
        column = columnEnds.length;
        columnEnds.push(end);
      } else {
        columnEnds[column] = end;
      }
      const span = Math.max(end - start, 0.55);
      const estimated =
        density === DENSITY.COMPACT
          ? COMPACT_MIN_CARD_HEIGHT_PX
          : estimateDetailedCardHeightPx(session);
      const measured = measuredHeights ? measuredHeights[session.key] || 0 : 0;
      return {
        session,
        start,
        end,
        span,
        column,
        minCardPx: Math.max(estimated, measured),
      };
    })
    .map((entry) => ({
      ...entry,
      columnCount: Math.max(columnEnds.length, 1),
    }));
}

/**
 * Empty hours stay short. An hour grows only when a session overlaps it and
 * the card needs more than that short row for its share of the span.
 */
function resolveHourHeightsPx(hours, laidOut) {
  const heightByHour = new Map();
  hours.forEach((hour) => heightByHour.set(hour, EMPTY_HOUR_HEIGHT_PX));

  laidOut.forEach(({ start, end, span, minCardPx }) => {
    const neededPerHour = minCardPx / span;
    hours.forEach((hour) => {
      if (end <= hour || start >= hour + 1) {
        return;
      }
      if (neededPerHour > heightByHour.get(hour)) {
        heightByHour.set(hour, neededPerHour);
      }
    });
  });

  hours.forEach((hour) => {
    heightByHour.set(hour, Math.ceil(heightByHour.get(hour)));
  });

  return heightByHour;
}

/** Pixel offset of a decimal hour, using each row’s own height. */
function offsetAtHour(time, hours, heightByHour) {
  let px = 0;
  for (let index = 0; index < hours.length; index += 1) {
    const hour = hours[index];
    const rowHeight = heightByHour.get(hour) || 0;
    if (time >= hour + 1) {
      px += rowHeight;
    } else if (time > hour) {
      px += (time - hour) * rowHeight;
      break;
    } else {
      break;
    }
  }
  return px;
}

function cardWidthPx(sessionKey, measuredWidths) {
  return measuredWidths[sessionKey] || FALLBACK_CARD_WIDTH_PX;
}

/**
 * Pack overlapping cards flush left using each card’s content width.
 * Column N starts where the widest overlapping card in columns 0..N-1 ends.
 */
function resolveLeftPx(item, laidOut, measuredWidths) {
  let left = EDGE_PAD_PX;
  for (let column = 0; column < item.column; column += 1) {
    let columnWidth = 0;
    laidOut.forEach((other) => {
      if (other.column !== column) {
        return;
      }
      if (other.end <= item.start || other.start >= item.end) {
        return;
      }
      columnWidth = Math.max(columnWidth, cardWidthPx(other.session.key, measuredWidths));
    });
    left += columnWidth;
  }
  return left;
}

/**
 * Card to bring into view for “now”: the ongoing session, otherwise the next
 * upcoming one. Returns null when nothing fits.
 */
function pickCurrentTimeCard(laidOut, nowHour) {
  if (!laidOut || laidOut.length === 0) {
    return null;
  }
  const ongoing = laidOut
    .filter((item) => item.start <= nowHour && item.end > nowHour)
    .sort((a, b) => a.start - b.start);
  if (ongoing.length > 0) {
    return ongoing[0];
  }
  const upcoming = laidOut
    .filter((item) => item.start >= nowHour)
    .sort((a, b) => a.start - b.start);
  if (upcoming.length > 0) {
    return upcoming[0];
  }
  return null;
}

/**
 * @param {Object} props
 * @param {Object[]} props.sessions Sessions for the selected day (all tutors).
 * @param {{startHour:number, endHour:number}} props.hourWindow
 * @param {string} props.selectedDate `YYYY-MM-DD`
 */
export default function TimelineDayView({
  sessions,
  hourWindow,
  selectedDate,
  density,
  onOpenSession,
  onStatusClick,
  onModeClick,
  onStudentClick,
  onTutorClick,
}) {
  const windowStart = hourWindow.startHour;
  const windowEnd = hourWindow.endHour;
  const hours = useMemo(() => {
    const list = [];
    for (let hour = windowStart; hour < windowEnd; hour += 1) {
      list.push(hour);
    }
    return list;
  }, [windowStart, windowEnd]);

  const isDetailed = density === DENSITY.DETAILED;
  const cardVariant = isDetailed ? 'detailed' : 'compact';
  const [measuredHeights, setMeasuredHeights] = useState({});
  const [measuredWidths, setMeasuredWidths] = useState({});
  const sessionNodeRefs = useRef(new Map());
  const timelineRef = useRef(null);
  const autoScrollKeyRef = useRef('');

  const daySessions = useMemo(() => {
    return (sessions || []).filter((session) => {
      if (session.startHour === null || session.startHour === undefined) {
        return false;
      }
      if (session.date && session.date !== selectedDate) {
        return false;
      }
      const endHour = sessionEndHour(session);
      return endHour > windowStart && session.startHour < hourWindow.endHour;
    });
  }, [sessions, selectedDate, windowStart, hourWindow.endHour]);

  const sessionKeysSignature = useMemo(
    () =>
      daySessions
        .map((session) => session.key)
        .filter(Boolean)
        .sort()
        .join('|'),
    [daySessions]
  );

  useEffect(() => {
    setMeasuredHeights({});
    setMeasuredWidths({});
    sessionNodeRefs.current = new Map();
  }, [sessionKeysSignature, selectedDate, density, windowStart, hourWindow.endHour]);

  const laidOut = useMemo(
    () =>
      layoutDaySessions(
        daySessions,
        windowStart,
        hourWindow.endHour,
        density,
        measuredHeights
      ),
    [daySessions, windowStart, hourWindow.endHour, density, measuredHeights]
  );

  const hourHeights = useMemo(
    () => resolveHourHeightsPx(hours, laidOut),
    [hours, laidOut]
  );

  const dayHeight = useMemo(() => {
    let total = 0;
    hours.forEach((hour) => {
      total += hourHeights.get(hour) || 0;
    });
    return total;
  }, [hours, hourHeights]);

  useLayoutEffect(() => {
    const nextHeights = {};
    const nextWidths = {};
    let heightsChanged = false;
    let widthsChanged = false;

    sessionNodeRefs.current.forEach((node, key) => {
      if (!node) {
        return;
      }
      const card = node.querySelector('.cv2-card');
      if (!card) {
        return;
      }
      const height = Math.ceil(card.scrollHeight);
      const width = Math.ceil(card.getBoundingClientRect().width);
      const previousHeight = measuredHeights[key] || 0;
      const previousWidth = measuredWidths[key] || 0;
      if (height > previousHeight + 1) {
        nextHeights[key] = height;
        heightsChanged = true;
      }
      if (Math.abs(width - previousWidth) > 1) {
        nextWidths[key] = width;
        widthsChanged = true;
      }
    });

    if (heightsChanged) {
      setMeasuredHeights((prev) => ({ ...prev, ...nextHeights }));
    }
    if (widthsChanged) {
      setMeasuredWidths((prev) => ({ ...prev, ...nextWidths }));
    }

    return undefined;
  }, [laidOut, dayHeight, measuredHeights, measuredWidths]);

  function bindSessionNode(key) {
    return (node) => {
      if (node) {
        sessionNodeRefs.current.set(key, node);
      } else {
        sessionNodeRefs.current.delete(key);
      }
    };
  }

  const dateMoment = Moment(selectedDate, 'YYYY-MM-DD');
  const headerLabel = dateMoment.isValid()
    ? dateMoment.format('ddd DD MMM YYYY')
    : selectedDate;

  useLayoutEffect(() => {
    const scroller = timelineRef.current;
    if (!scroller || hours.length === 0) {
      return undefined;
    }

    const timezone = sessionStorage.getItem('timezone_identifier');
    const now = timezone ? Moment().tz(timezone) : Moment();
    const selected = Moment(selectedDate, 'YYYY-MM-DD');
    if (!selected.isValid() || !selected.isSame(now, 'day')) {
      return undefined;
    }

    const scrollKey = `${selectedDate}|${density}|${sessionKeysSignature}`;
    if (autoScrollKeyRef.current === scrollKey) {
      return undefined;
    }

    const nowHour = now.hour() + now.minute() / 60 + now.second() / 3600;
    const focusCard = pickCurrentTimeCard(laidOut, nowHour);
    const focusHour = focusCard
      ? focusCard.start
      : Math.min(Math.max(nowHour, windowStart), windowEnd - 0.01);
    const targetTop = focusCard
      ? offsetAtHour(focusCard.start, hours, hourHeights)
      : offsetAtHour(focusHour, hours, hourHeights);

    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scroller.scrollTop = Math.min(Math.max(0, targetTop - 8), maxScroll);
    autoScrollKeyRef.current = scrollKey;
    return undefined;
  }, [
    selectedDate,
    density,
    sessionKeysSignature,
    hours,
    hourHeights,
    laidOut,
    windowStart,
    windowEnd,
  ]);

  return (
    <div
      className={`cv2-week cv2-day-timeline${
        isDetailed ? ' cv2-day-timeline--detailed' : ' cv2-day-timeline--compact'
      }`}
      ref={timelineRef}
    >
      <div className="cv2-week__grid cv2-day-timeline__grid">
        <div className="cv2-week__head">Time</div>
        <div className="cv2-week__head">{headerLabel}</div>

        <div className="cv2-week__times" style={{ height: dayHeight }}>
          {hours.map((hour) => (
            <div className="cv2-week__time" key={hour} style={{ height: hourHeights.get(hour) }}>
              {hourLabel(hour)}
            </div>
          ))}
        </div>

        <div className="cv2-week__day cv2-day-timeline__day" style={{ height: dayHeight }}>
          {hours.map((hour) => (
            <div
              className="cv2-week__hour-line"
              key={hour}
              style={{
                top: offsetAtHour(hour, hours, hourHeights),
                height: hourHeights.get(hour),
              }}
            />
          ))}

          {laidOut.map((item) => {
            const { session, start } = item;
            const top = offsetAtHour(start, hours, hourHeights);
            const left = resolveLeftPx(item, laidOut, measuredWidths);

            return (
              <div
                className="cv2-week__session"
                key={session.key}
                ref={bindSessionNode(session.key)}
                style={{
                  top,
                  height: 'auto',
                  left,
                  width: 'max-content',
                  maxWidth: 'none',
                  right: 'auto',
                }}
              >
                <SessionCard
                  session={session}
                  variant={cardVariant}
                  tutorInitials={initialsOf(session.tutorName)}
                  showTutorName
                  onOpen={onOpenSession}
                  onStatusClick={onStatusClick}
                  onModeClick={onModeClick}
                  onStudentClick={onStudentClick}
                  onTutorClick={onTutorClick}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

TimelineDayView.propTypes = {
  sessions: PropTypes.array.isRequired,
  hourWindow: PropTypes.shape({
    startHour: PropTypes.number.isRequired,
    endHour: PropTypes.number.isRequired,
  }).isRequired,
  selectedDate: PropTypes.string.isRequired,
  density: PropTypes.string.isRequired,
  onOpenSession: PropTypes.func.isRequired,
  onStatusClick: PropTypes.func.isRequired,
  onModeClick: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func.isRequired,
  onTutorClick: PropTypes.func.isRequired,
};
